import { apiClient } from "@/lib/api/http"
import type { Company } from "@/lib/api/company.api"

// ── Types ───────────────────────────────────────────────────────────────────────
export interface SellerProfile {
  id: string
  userId: string
  companyId: string | null
  designation: string
  department: string
  phoneNumber: string
  bio: string
  linkedinUrl?: string
  secondaryPhoneNumber?: string
  yearsOfExperience?: number
  employeeRole: string
  isActive: boolean
  verificationStatus: string
  createdAt: string
  updatedAt: string
  company: Company | null
}

export interface CreateSellerProfilePayload {
  designation: string
  department: string
  phoneNumber: string
  bio: string
}

// ── Envelope wrapper (backend wraps all GET responses) ──────────────────────────
interface Envelope<T> {
  success: boolean
  statusCode: number
  message: string
  data: T
}

// ── GET /seller/{id} — profile completeness check (login flow) ──────────────────
// Returns null when SELLER_PROFILE_INCOMPLETE, throws everything else
export async function getSellerProfile(
  id: string,
  token: string
): Promise<SellerProfile | null> {
  try {
    const { data: envelope } = await apiClient.get<Envelope<SellerProfile>>(
      `/seller/${id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
    return envelope.data
  } catch (error: unknown) {
    const errCode =
      (error as { code?: string })?.code ??
      (error as { errorCode?: string })?.errorCode

    if (errCode === "SELLER_PROFILE_INCOMPLETE") return null
    throw error
  }
}

// ── GET /seller/{id} — full profile for display in dashboard ────────────────────
export async function getSellerProfileById(
  id: string,
  token: string
): Promise<SellerProfile> {
  const { data: envelope } = await apiClient.get<Envelope<SellerProfile>>(
    `/seller/${id}`,
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return envelope.data
}

// ── POST /seller ────────────────────────────────────────────────────────────────
export async function createSellerProfile(
  payload: CreateSellerProfilePayload,
  token: string
): Promise<SellerProfile> {
  const { data: envelope } = await apiClient.post<any>(
    "/seller",
    payload,
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return envelope?.data ?? envelope
}

// ── PATCH /seller/{id} ──────────────────────────────────────────────────────────
export interface UpdateSellerProfilePayload {
  designation?: string
  department?: string
  phoneNumber?: string
  bio?: string
  linkedinUrl?: string
  secondaryPhoneNumber?: string
  yearsOfExperience?: number
}

export async function updateSellerProfile(
  id: string,
  payload: UpdateSellerProfilePayload,
  token: string
): Promise<SellerProfile> {
  const { data: envelope } = await apiClient.patch<Envelope<SellerProfile>>(
    `/seller/${id}`,
    payload,
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return envelope.data
}
// ── GET /analytics/seller/kpis ────────────────────────────────────────────────
export interface SellerKpiMetric {
  value: number
  previousValue?: number
  formattedValue?: string
  currency?: string
  trend: string
  trendPercentage: number
  trendDirection: "up" | "down" | "neutral"
  sparkline: number[]
  breakdown?: {
    pendingRfqs?: number
    pendingMilestones?: number
    [key: string]: any
  }
}

export interface SellerKpiTimeframeItem {
  timeframe: string
  periodLabel?: string
  kpis: {
    activeQuotations: SellerKpiMetric
    activeOrders: SellerKpiMetric
    awaitingAction: SellerKpiMetric
    confirmedValue: SellerKpiMetric
    [key: string]: any
  }
}

export interface SellerKpisResponse {
  timeframes: SellerKpiTimeframeItem[]
}

export async function getSellerKpis(token: string): Promise<SellerKpisResponse> {
  const { data: envelope } = await apiClient.get<Envelope<SellerKpisResponse>>(
    "/analytics/seller/kpis",
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  )
  return envelope.data
}
// ── GET /analytics/seller/business-activity ───────────────────────────────────
export interface SellerBusinessActivityPoint {
  date: string
  timestamp?: string
  rfqsCreated: number
  quotationsReceived: number
  ordersPlaced: number
}

export interface SellerBusinessActivitySummary {
  totalRfqs: number
  totalQuotations: number
  totalOrders: number
  conversionRate: string
}

export interface SellerBusinessActivityResponse {
  summary?: SellerBusinessActivitySummary
  timeframes: Record<string, SellerBusinessActivityPoint[] | { summary?: SellerBusinessActivitySummary; points: SellerBusinessActivityPoint[] }>
}

export async function getSellerBusinessActivity(
  token: string
): Promise<SellerBusinessActivityResponse> {
  const { data: envelope } = await apiClient.get<Envelope<SellerBusinessActivityResponse>>(
    "/analytics/seller/business-activity",
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  )
  return envelope.data
}
// ── GET /analytics/seller/action-center ───────────────────────────────────────
export interface SellerActionCenterItem {
  id: string
  category: "RFQS" | "ORDERS" | "DEADLINES" | "COMPLAINTS" | string
  count: number
}

export interface SellerActionCenterData {
  items: SellerActionCenterItem[]
}

export async function getSellerActionCenter(
  token: string
): Promise<SellerActionCenterData> {
  const { data: envelope } = await apiClient.get<Envelope<SellerActionCenterData>>(
    "/analytics/seller/action-center",
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  )
  return envelope.data
}

// ── GET /analytics/seller/rfq-journey ─────────────────────────────────────────
export interface SellerRfqJourneyStage {
  id: string
  stageNumber: number
  label: string
  count: number
  percentage: number
}

export interface SellerRfqJourneyData {
  stages: SellerRfqJourneyStage[]
  summary?: {
    overallConversionRate: string
    conversionTrend: string
    trendDirection: "up" | "down" | "neutral" | string
  }
}

export async function getSellerRfqJourney(
  token: string
): Promise<SellerRfqJourneyData> {
  const { data: envelope } = await apiClient.get<Envelope<SellerRfqJourneyData>>(
    "/analytics/seller/rfq-journey",
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  )
  return envelope.data
}
